import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { blurOnEnter } from "@/lib/blurOnEnter";
import { showSaveFailedToast } from "@/lib/saveFailureToast";
import SavedIndicator, { useSavedFlash } from "@/components/shared/SavedIndicator";
import { useAuth } from "@/hooks/useAuth";
import { useIsPro } from "@/hooks/useIsPro";
import { useGoogleCalendar } from "@/hooks/useGoogleCalendar";
import { usePreferences } from "@/hooks/useUserPreferences";
import { ProUpsellLink } from "@/components/ProUpsell";
import { reportError } from "@/lib/reportError";
import { resolveSavedZip } from "@/lib/userLocation";
import {
  chipLabel,
  entriesToStored,
  migrateStoredTickers,
  resolveTickerInput,
  type MigratedEntry,
  type StoredTicker,
  type TickerResolution,
} from "@/lib/tickerResolve";

interface BriefingPrefs {
  // #159: `zip` is LEGACY here. This UI no longer writes it — her zip lives on
  // `user_preferences.zip_code`, the one field the whole app reads. The key
  // stays in the type because accounts that set it before #159 still carry it
  // and the dispatcher still reads it as a fallback. lat/lon are the
  // dispatcher's geocode cache, stamped with `geocoded_for`.
  weather?:  { on: boolean; zip?: string; lat?: number; lon?: number; geocoded_for?: string };
  news?:     { on: boolean; topics?: string[] };
  // #440 — `tickers` used to be a plain `string[]` of whatever survived a
  // character filter, which is how "AMAZON" and "SEPHORA" got saved as tickers.
  // It now holds `{symbol, display}`; older accounts still hold strings and are
  // re-resolved on open rather than migrated by SQL (there is none in #440).
  stocks?:   { on: boolean; tickers?: StoredTicker[] };
  movement?: { on: boolean; goal?: string; minutes?: number; window?: "morning" | "afternoon" | "evening"; auto_block?: boolean };
  faith?:    { on: boolean; mode?: "devotional" | "gratitude" | "both" };
  // #1898 — tonight's dinner off her committed meal plan. Toggle only, no
  // fields. DEFAULT OFF, which is Kasandra's call rather than a convention:
  // "I don't really want that in my own morning note."
  dinner?:   { on: boolean };
}

const FONT = "'Plus Jakarta Sans', sans-serif";

const ProBadge = () => (
  <span style={{
    backgroundColor: "#5050C8", color: "white", borderRadius: 999,
    fontSize: 9, fontWeight: 600, padding: "2px 6px", lineHeight: 1.4,
    fontFamily: FONT, whiteSpace: "nowrap" as const, flexShrink: 0,
  }}>
    ⚡ PRO
  </span>
);

function Toggle({ on, onChange }: { on: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={onChange}
      style={{
        position: "relative", width: 44, height: 26, borderRadius: 999,
        background: on ? "#5050C8" : "#D8D8E8",
        border: "none", padding: 0, flexShrink: 0,
        cursor: "pointer", transition: "background 0.2s ease",
      }}
    >
      <span style={{
        position: "absolute", top: 3, left: on ? 21 : 3,
        width: 20, height: 20, borderRadius: "50%", background: "#FFFFFF",
        transition: "left 0.2s ease", boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
      }} />
    </button>
  );
}

// How long the already-here chip stays lit after a duplicate is rejected.
// Matched to the "Saved ✓" flash below so the card only has one flash tempo.
const DUPE_FLASH_MS = 1500;

function ChipEntry({ chips, onAdd, onRemove, placeholder, max, resolve, labelFor, testId, autoCapitalize = "none" }: {
  /** The stored value of each chip — for stocks this is the SYMBOL, not the label. */
  chips: string[];
  onAdd: (v: string, display?: string) => void;
  onRemove: (v: string) => void;
  placeholder: string;
  max: number;
  /**
   * #440 — turns what she typed into what gets stored, or into a sentence
   * explaining why it can't be. News passes nothing and keeps its old
   * behaviour: whatever she typed is the chip.
   */
  resolve?: (raw: string) => TickerResolution;
  /** What the chip reads, when that differs from the stored value. */
  labelFor?: (v: string) => string;
  /** News and Stocks both promise "press Enter" in their placeholder, so a
   *  placeholder is not a unique handle for either from an e2e spec. */
  testId?: string;
  autoCapitalize?: "none" | "characters" | "words";
}) {
  const [input, setInput] = useState("");
  // #440 — the resolver's refusal, held until she edits the box. Unlike the
  // duplicate flash below this has NO timer: a duplicate is self-explanatory
  // once you see the chip light up, but "Sephora isn't traded on its own" is an
  // answer she may need to read twice and then act on.
  const [rejection, setRejection] = useState<string | null>(null);
  const rejectionId = useId();
  // The chip a just-rejected duplicate matched, or null. Issue #536: typing a
  // ticker you already have used to clear the box and do nothing else, which
  // from her side is indistinguishable from Enter being broken. Now her text
  // stays put and the chip she already has lights up, so the reason is visible
  // without a word of copy — which matters because ChipEntry is generic (News →
  // Topics used it before that row went coming-soon, and will again), so any
  // wording would have to be passed in rather than living here.
  const [dupe, setDupe] = useState<string | null>(null);
  const dupeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDupe = () => {
    if (dupeRef.current) { clearTimeout(dupeRef.current); dupeRef.current = null; }
    setDupe(null);
  };
  // Unmount cancels the timer: at `max` the whole input disappears (see the
  // chips.length < max guard below), and removing the last chip unmounts the
  // chip row — either can happen mid-flash, and a setState after that is a leak.
  useEffect(() => () => { if (dupeRef.current) clearTimeout(dupeRef.current); }, []);

  const commit = () => {
    const raw = input.trim();
    if (!raw) return;
    let val = raw;
    let display: string | undefined;
    if (resolve) {
      const res = resolve(raw);
      if (res.ok === false) {
        // Keep her text. Same reasoning as the duplicate branch below: clearing
        // the box under a message about what she typed makes the message
        // unactionable, and #440's whole point is that she can fix it here.
        if (res.message) setRejection(res.message);
        return;
      }
      val = res.symbol;
      display = res.display;
      setRejection(null);
    }
    // Duplicate is the one bail-out that owes her an explanation, so it gets
    // handled first and on its own terms: keep her text so she can see and edit
    // what she typed, and flash the existing chip. Deliberately NOT clearing the
    // box here is the whole fix. The other three bail-outs stay silent and keep
    // clearing, exactly as before — nothing typed, normalizes to nothing, or at
    // cap (unreachable from this box: the input has already unmounted at max).
    if (val && chips.includes(val)) {
      setDupe(val);
      if (dupeRef.current) clearTimeout(dupeRef.current);
      dupeRef.current = setTimeout(() => { dupeRef.current = null; setDupe(null); }, DUPE_FLASH_MS);
      return;
    }
    if (!val || chips.length >= max) { setInput(""); return; }
    clearDupe();
    onAdd(val, display);
    setInput("");
  };
  return (
    <div>
      {chips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5, marginBottom: 8 }}>
          {chips.map((c) => (
            <span key={c} style={{
              background: c === dupe ? "#C8C8F4" : "#F0F0FF",
              border: `1px solid ${c === dupe ? "#5050C8" : "#C8C8F4"}`,
              borderRadius: 999,
              fontSize: 12, color: "#1C1A3E", padding: "3px 8px",
              display: "flex", alignItems: "center", gap: 4, fontFamily: FONT,
              transition: "background 0.15s ease, border-color 0.15s ease",
            }}>
              {labelFor ? labelFor(c) : c}
              <button
                type="button"
                onClick={() => onRemove(c)}
                // Deliberately the STORED value, not the label — #536's tests
                // and every spec that removes a chip address it by symbol, and
                // a label is a display concern that can change under them.
                aria-label={`Remove ${c}`}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#54525C", padding: 0, lineHeight: 1, fontSize: 14, marginTop: -1 }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {chips.length < max && (
        <input
          type="text"
          data-testid={testId}
          value={input}
          // The flash is an answer to "why didn't that work" — the moment she
          // edits what she typed, the question is gone and so is the highlight.
          onChange={(e) => { setInput(e.target.value); if (dupe) clearDupe(); if (rejection) setRejection(null); }}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } }}
          onBlur={commit}
          // Wordless "this one was rejected" for screen readers, which can't see
          // the chip light up. The duplicate case attaches no message on purpose
          // — see the dupe state above. #440's refusal DOES have one, so it gets
          // described, which is also what makes it announced rather than merely
          // visible.
          aria-invalid={dupe !== null || rejection !== null || undefined}
          aria-describedby={rejection ? rejectionId : undefined}
          placeholder={placeholder}
          // enterKeyHint labels the iOS return key "Go" — the placeholder promises
          // Enter does something, so the key should say so too. We deliberately do
          // NOT blur here: commit() leaves focus alone (and, on a duplicate, leaves
          // her text alone too), so the keyboard stays up for the next chip.
          enterKeyHint="go"
          // iOS autocorrect is why "press Enter" felt dead on a phone. With a live
          // suggestion in the bar, the return key ACCEPTS the suggestion instead of
          // firing a clean Enter keydown — so the text silently changes and no chip
          // is added. Still off after #440 made this box accept company names as
          // well as symbols: brand names are exactly what autocorrect mangles
          // ("Kroger" → "Krogen"), and this field commits on Enter, so an eaten
          // keydown is the difference between a chip and nothing.
          autoCorrect="off"
          autoCapitalize={autoCapitalize}
          spellCheck={false}
          style={{
            width: "100%", fontSize: 13, color: "#1C1A3E",
            border: "1.5px solid #C8C8F4", borderRadius: 10, padding: "7px 10px",
            background: "#FAFAFF", fontFamily: FONT, boxSizing: "border-box" as const,
          }}
        />
      )}
      {rejection && (
        // Sits UNDER the box she is still looking at, with her text still in it.
        // role="status" rather than "alert": she did nothing wrong, Kin just
        // can't follow that one, and an assertive interrupt would land as a
        // telling-off in a settings screen.
        <p
          id={rejectionId}
          role="status"
          className="font-body text-xs mt-2"
          style={{ color: "#8A5A00", lineHeight: 1.45 }}
        >
          {rejection}
        </p>
      )}
    </div>
  );
}

const Divider = () => <div style={{ height: 1, backgroundColor: "#E8E8F4", margin: "14px 0" }} />;

// Enter on a plain text field (goal, zip): drop the keyboard, exactly like
// tapping outside the box already does. Every field in here writes through the
// same 800ms debounced persist() on change, so there is no separate "save" to
// fire — blurring IS the commit, and "Saved ✓" lands a beat later either way.
// preventDefault is belt-and-braces: none of these inputs sits inside a <form>
// today (the only three in src/ are the auth pages), but if one ever gets
// wrapped, Enter must not turn into an implicit submit and reload the page.
const WINDOW_OPTIONS = [
  { value: "morning" as const, label: "Morning" },
  { value: "afternoon" as const, label: "Afternoon" },
  { value: "evening" as const, label: "Evening" },
];
const FAITH_OPTIONS = [
  { value: "devotional" as const, label: "Devotional" },
  { value: "gratitude" as const, label: "Gratitude" },
  { value: "both" as const, label: "Both" },
];

const pilLabelStyle = (selected: boolean) => ({
  padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
  background: selected ? "#5050C8" : "#F0F0FF",
  color: selected ? "#FFFFFF" : "#54525C",
  border: selected ? "1.5px solid #5050C8" : "1.5px solid #C8C8F4",
  cursor: "pointer", fontFamily: FONT,
} as const);

const BriefingBuilder = () => {
  const { user } = useAuth();
  const { prefs: contextPrefs, loading: prefsLoading, updatePreferences } = usePreferences();
  const isPro = useIsPro();
  const { connected: gcalConnected, connect: gcalConnect } = useGoogleCalendar();
  const [prefs, setPrefs] = useState<BriefingPrefs>({});
  const [loaded, setLoaded] = useState(false);
  // #537 — the flash and its timer now live in the shared hook; this was the
  // app's only saved-indicator and is now its first consumer.
  const { saved: savedFlash, showSaved } = useSavedFlash();
  const [proNudge, setProNudge] = useState<string | null>(null);
  // #159 — the zip field's own draft. Her saved zip is a COLUMN now, not part
  // of `prefs`, so it needs state of its own. Partial digits live here and are
  // never persisted: `kin-chat` puts `zip_code` straight into the prompt
  // ("Her zip code is …"), and half a zip is worse there than no zip.
  const [zipDraft, setZipDraft] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nudgeRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (prefsLoading) return;
    setPrefs((contextPrefs.briefing_prefs as BriefingPrefs) ?? {});
    // Seeded through the same resolver every other card uses, so an account
    // that only ever set the old Briefing Builder zip still sees it here.
    setZipDraft(resolveSavedZip(contextPrefs) ?? "");
    setLoaded(true);
  }, [prefsLoading]);

  const persist = (next: BriefingPrefs) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // updatePreferences RESOLVES the PostgrestError rather than throwing
      // (#416), so it has to be CHECKED. This used to print "Saved ✓"
      // unconditionally, which made the #537 fix for this very card an
      // instance of #545: a confirmation is a CLAIM ABOUT THE DATABASE, and
      // adding one to a write nobody checks does not remove the silence — it
      // replaces it with a false statement, which is worse and much harder to
      // notice than no feedback at all.
      const error = await updatePreferences({ briefing_prefs: next });
      if (error) {
        reportError("[BriefingBuilder] briefing_prefs save failed", error, { tags: { area: "briefing-builder" } });
        showSaveFailedToast();
        return; // no green check over a row that was never written
      }
      showSaved();
    }, 800);
  };

  const update = (key: keyof BriefingPrefs, patch: Record<string, any>) => {
    const next = { ...prefs, [key]: { ...(prefs[key] as any ?? {}), ...patch } };
    setPrefs(next);
    persist(next);
  };

  /**
   * #159 — the zip field now writes `user_preferences.zip_code`, the single
   * field the whole app reads (Pro Setup, kin-chat, Event Finder, Kroger,
   * the planners). Before this there were two zips in Settings that could hold
   * different values, and only this one fed the weather line.
   *
   * ⚠️ Three things happen in ONE write, and each is load-bearing:
   *
   *  1. `zip_code` gets the new value — canonical.
   *  2. the LEGACY `briefing_prefs.weather.zip` is stripped, so this account can
   *     no longer hold two zips that disagree. Only the key is dropped; the rest
   *     of `weather` (notably `on`) is preserved, because losing `on` here would
   *     silently turn her weather line off as a side effect of typing a zip.
   *  3. the dispatcher's geocode cache (lat/lon/geocoded_for) is dropped, so the
   *     next run re-geocodes rather than reporting the old city.
   *
   * One `updatePreferences` call rather than two, so a half-applied change can't
   * leave `zip_code` updated with the stale legacy value still shadowing nothing.
   *
   * ⚠️ A PARTIAL zip is never persisted. `kin-chat` interpolates `zip_code`
   * directly into the system prompt, so "902" would become a claim about where
   * she lives. Partial digits stay in `zipDraft` until they make five.
   */
  const handleZipChange = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 5);
    setZipDraft(digits);
    if (digits.length !== 0 && digits.length !== 5) return; // mid-type
    const { zip: _zip, lat: _lat, lon: _lon, geocoded_for: _g, ...restWeather } =
      (prefs.weather ?? {}) as any;
    const nextPrefs = { ...prefs, weather: restWeather };
    setPrefs(nextPrefs);
    persistZipAndPrefs(digits === "" ? null : digits, nextPrefs);
  };

  /** Shares `persist`'s debounce ref so the two writers cannot race on briefing_prefs. */
  const persistZipAndPrefs = (zip: string | null, nextPrefs: BriefingPrefs) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      // Same #416/#545 discipline as `persist`: updatePreferences RESOLVES the
      // error rather than throwing, so a green check over an unchecked write is
      // a false statement about the database.
      const error = await updatePreferences({ zip_code: zip, briefing_prefs: nextPrefs });
      if (error) {
        reportError("[BriefingBuilder] zip save failed", error, { tags: { area: "briefing-builder" } });
        showSaveFailedToast();
        return;
      }
      showSaved();
    }, 800);
  };

  const handleToggle = (key: keyof BriefingPrefs, isProFeature: boolean) => {
    if (isProFeature && !isPro) {
      setProNudge(key as string);
      if (nudgeRef.current) clearTimeout(nudgeRef.current);
      // Issue #50: the nudge is now a tap-through to /pro, not dead-end text,
      // so it has to stay up long enough to actually tap. Deliberately NOT an
      // immediate navigate() like TasksTab's showProGate() — she's mid-way
      // through configuring several toggles here, and yanking her off the page
      // on one mis-tap loses that context. Gate evaluation is unchanged.
      nudgeRef.current = setTimeout(() => setProNudge(null), 6000);
      return;
    }
    update(key, { on: !((prefs[key] as any)?.on ?? false) });
  };

  // ── #440: the stock watchlist ────────────────────────────────────────────
  // Everything stored, re-resolved on every render. Cheap (a Map lookup per
  // entry) and it means the chips and the stored array cannot drift apart.
  const storedTickers = (prefs.stocks as any)?.tickers;
  const tickerView = useMemo(() => migrateStoredTickers(storedTickers), [storedTickers]);
  const tickerSymbols = tickerView.entries.flatMap((e) => (e.kind === "ok" ? [e.ticker.symbol] : []));
  const tickerProblems = tickerView.entries.flatMap((e) => (e.kind === "problem" ? [e] : []));
  const stocksPanelOpen = isPro && ((prefs.stocks as any)?.on ?? false);

  const writeTickers = (entries: MigratedEntry[]) => {
    const next = { ...prefs, stocks: { ...((prefs.stocks as any) ?? {}), tickers: entriesToStored(entries) } };
    setPrefs(next);
    persist(next);
  };

  /**
   * #440 point 3 — the one-time re-resolve of what is already saved.
   *
   * Runs once per mount, and only while the panel is actually on screen. A
   * background rewrite of a free account's prefs would be a write with no
   * visible cause, and it would flash "Saved ✓" at someone who opened Settings
   * to do something else entirely.
   *
   * It writes only when something genuinely changed, so re-opening Settings all
   * day is one write, not one per open. Unresolvable strings are NOT part of
   * "changed" — they are kept exactly as stored and re-surfaced every open,
   * which is the point: dropping "SEPHORA" quietly would be the same silent
   * failure wearing a different coat.
   */
  const migratedRef = useRef(false);
  useEffect(() => {
    if (!loaded || migratedRef.current || !stocksPanelOpen) return;
    migratedRef.current = true;
    if (!tickerView.changed) return;
    writeTickers(tickerView.entries);
    // `prefs` is deliberately out of the dep list — this fires on the first
    // render where the panel is open and never again for this mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, stocksPanelOpen]);

  // One nudge renderer for all four Pro rows. Copy note: this used to read
  // "Kin Pro — coming soon 💜", which was stale — these toggles already work
  // for Pro users, so nothing about them is coming soon.
  const proNudgeFor = (key: string) =>
    proNudge === key ? (
      <div className="mt-1">
        <ProUpsellLink label="Kin Pro feature — see what's included 💜" fontSize={12} />
      </div>
    ) : null;

  if (!loaded) return null;

  const w = (prefs.weather ?? {}) as any;
  const n = (prefs.news ?? {}) as any;
  const s = (prefs.stocks ?? {}) as any;
  const m = (prefs.movement ?? {}) as any;
  const f = (prefs.faith ?? {}) as any;
  const d = (prefs.dinner ?? {}) as any;

  return (
    <section className="mt-6" id="morning-builder">
      <h2 className="font-body text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#54525C" }}>
        ☀️ Morning Briefing
      </h2>
      <div className="bg-white" style={{ border: "1.5px solid #C8C8F4", borderRadius: 16, padding: 18 }}>
        <p className="font-body text-xs mb-4" style={{ color: "#54525C" }}>
          Pick what shows up in your morning notes.
        </p>

        {/* Weather — free */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🌤️</span>
            <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E", flex: 1 }}>Daily weather</span>
            <Toggle on={w.on ?? false} onChange={() => handleToggle("weather", false)} />
          </div>
          {w.on && (
            <div style={{ marginTop: 10, paddingLeft: 26 }}>
              <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Your zip code (US)</p>
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                value={zipDraft}
                onChange={(e) => handleZipChange(e.target.value)}
                onKeyDown={blurOnEnter}
                enterKeyHint="done"
                placeholder="e.g. 90210"
                style={{
                  fontSize: 13, color: "#1C1A3E", border: "1.5px solid #C8C8F4",
                  borderRadius: 10, padding: "7px 10px", background: "#FAFAFF",
                  fontFamily: FONT, width: 130,
                }}
              />
              <p className="font-body text-xs mt-1" style={{ color: "#8B8B9E" }}>Free daily forecast — no account needed. This is the zip Kin uses everywhere.</p>
            </div>
          )}
        </div>

        <Divider />

        {/* News — Pro. Restored Aug 19 2026 (#449 reverted).
            #449 replaced this toggle with a "Coming soon ✨" pill on Aug 2,
            stating news "isn't shipped yet". That was already wrong: the
            dispatcher gained `newsOn` + web search on Jul 3 (9162912) and has
            never been changed since, so news has been delivered the whole time
            — verified in production, where the one account that still had
            news.on receives it daily in her morning text.
            Hiding it meant 134 of 135 accounts could not switch on a working
            feature the Pro page correctly advertises. News and stocks share one
            path (`useWebSearch = newsOn || stocksOn`), and stocks shipped with a
            live toggle throughout, so there was never a capability or cost
            argument that applied to one and not the other. */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>📰</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>News briefing</span>
              <ProBadge />
            </div>
            <Toggle on={isPro && (n.on ?? false)} onChange={() => handleToggle("news", true)} />
          </div>
          {proNudgeFor("news")}
          {isPro && n.on && (
            <div style={{ marginTop: 10, paddingLeft: 26 }}>
              <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Topics (up to 5)</p>
              <ChipEntry
                chips={n.topics ?? []}
                onAdd={(v) => update("news", { topics: [...(n.topics ?? []), v] })}
                onRemove={(v) => update("news", { topics: (n.topics ?? []).filter((x: string) => x !== v) })}
                placeholder="e.g. parenting, health — press Enter"
                max={5}
              />
            </div>
          )}
        </div>

        <Divider />

        {/* Stocks — Pro */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>📈</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Stock watchlist</span>
              <ProBadge />
            </div>
            <Toggle on={isPro && (s.on ?? false)} onChange={() => handleToggle("stocks", true)} />
          </div>
          {proNudgeFor("stocks")}
          {stocksPanelOpen && (
            <div style={{ marginTop: 10, paddingLeft: 26 }}>
              <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Companies or tickers (up to 10)</p>
              {/* Anything already saved that Kin can't follow. Kept on screen —
                  and in the database, untouched — until she decides what to do
                  with it. Each one carries the same sentence the box would have
                  given her had she typed it today. */}
              {tickerProblems.map((p) => (
                <div key={p.raw} style={{ marginBottom: 8 }}>
                  <span style={{
                    background: "#FFF4E0", border: "1px solid #E8C88A", borderRadius: 999,
                    fontSize: 12, color: "#5A4000", padding: "3px 8px",
                    display: "inline-flex", alignItems: "center", gap: 4, fontFamily: FONT,
                  }}>
                    {p.raw}
                    <button
                      type="button"
                      onClick={() => writeTickers(tickerView.entries.filter((e) => e !== p))}
                      aria-label={`Remove ${p.raw}`}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#8A5A00", padding: 0, lineHeight: 1, fontSize: 14, marginTop: -1 }}
                    >
                      ×
                    </button>
                  </span>
                  <p className="font-body text-xs mt-1" style={{ color: "#8A5A00", lineHeight: 1.45 }}>{p.message}</p>
                </div>
              ))}
              <ChipEntry
                chips={tickerSymbols}
                labelFor={(sym) => {
                  const hit = tickerView.entries.find((e) => e.kind === "ok" && e.ticker.symbol === sym);
                  return hit && hit.kind === "ok" ? chipLabel(hit.ticker) : sym;
                }}
                resolve={resolveTickerInput}
                onAdd={(sym, display) => writeTickers([...tickerView.entries, { kind: "ok", ticker: { symbol: sym, display: display ?? sym } }])}
                onRemove={(sym) => writeTickers(tickerView.entries.filter((e) => !(e.kind === "ok" && e.ticker.symbol === sym)))}
                placeholder="e.g. Amazon, or AAPL, then press Enter"
                max={10 - tickerProblems.length}
                testId="stocks-ticker-input"
              />
              <p className="font-body text-xs mt-1" style={{ color: "#8B8B9E" }}>
                Type a company name and Kin finds the ticker. Symbols work too.
              </p>
            </div>
          )}
        </div>

        <Divider />

        {/* Movement — Pro */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🚲</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Movement goal</span>
              <ProBadge />
            </div>
            <Toggle on={isPro && (m.on ?? false)} onChange={() => handleToggle("movement", true)} />
          </div>
          {proNudgeFor("movement")}
          {isPro && m.on && (
            <div style={{ marginTop: 10, paddingLeft: 26, display: "flex", flexDirection: "column" as const, gap: 10 }}>
              <div>
                <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Your goal</p>
                <input
                  type="text"
                  value={m.goal ?? ""}
                  onChange={(e) => update("movement", { goal: e.target.value })}
                  onKeyDown={blurOnEnter}
                  enterKeyHint="done"
                  placeholder="e.g. Walk 10,000 steps"
                  style={{
                    fontSize: 13, color: "#1C1A3E", border: "1.5px solid #C8C8F4",
                    borderRadius: 10, padding: "7px 10px", background: "#FAFAFF",
                    fontFamily: FONT, width: "100%", boxSizing: "border-box" as const,
                  }}
                />
              </div>
              <div>
                <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Duration (minutes)</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                  {[15, 20, 30, 45, 60].map((min) => (
                    <button key={min} type="button" onClick={() => update("movement", { minutes: min })}
                      style={pilLabelStyle((m.minutes ?? 30) === min)}>
                      {min}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="font-body text-xs mb-1" style={{ color: "#54525C" }}>Best time</p>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                  {WINDOW_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button" onClick={() => update("movement", { window: opt.value })}
                      style={pilLabelStyle((m.window ?? "morning") === opt.value)}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                {gcalConnected ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <p className="font-body text-sm" style={{ color: "#1C1A3E" }}>Auto-block on Google Calendar</p>
                      <p className="font-body text-xs" style={{ color: "#8B8B9E" }}>Kin blocks the slot before your day starts.</p>
                    </div>
                    <Toggle on={m.auto_block ?? false} onChange={() => update("movement", { auto_block: !(m.auto_block ?? false) })} />
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <p className="font-body text-xs" style={{ color: "#8B8B9E", flex: 1 }}>Connect Google Calendar to auto-block your movement slot.</p>
                    <button type="button" onClick={gcalConnect}
                      className="font-body font-semibold text-xs flex-shrink-0"
                      style={{ color: "#5050C8", background: "none", border: "none", cursor: "pointer", padding: "4px 0", textDecoration: "underline", fontFamily: FONT }}>
                      Connect →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Divider />

        {/* Faith — Pro */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🙏</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Faith moment</span>
              <ProBadge />
            </div>
            <Toggle on={isPro && (f.on ?? false)} onChange={() => handleToggle("faith", true)} />
          </div>
          {proNudgeFor("faith")}
          {isPro && f.on && (
            <div style={{ marginTop: 10, paddingLeft: 26 }}>
              <p className="font-body text-xs mb-2" style={{ color: "#54525C" }}>What feeds your spirit?</p>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" as const }}>
                {FAITH_OPTIONS.map((opt) => (
                  <button key={opt.value} type="button" onClick={() => update("faith", { mode: opt.value })}
                    style={pilLabelStyle((f.mode ?? "devotional") === opt.value)}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="font-body text-xs mt-2" style={{ color: "#8B8B9E" }}>
                Devotional: a Bible verse. Gratitude: a reflection prompt. Both: you get both.
              </p>
            </div>
          )}
        </div>

        <Divider />

        {/* Tonight's dinner — Pro (#1898) */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>🍽️</span>
            <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span className="font-body text-sm font-semibold" style={{ color: "#1C1A3E" }}>Tonight's dinner</span>
              <ProBadge />
            </div>
            <Toggle on={isPro && (d.on ?? false)} onChange={() => handleToggle("dinner", true)} />
          </div>
          {proNudgeFor("dinner")}
          {/* Toggle only — there is nothing to configure. The meal comes from the
              meal plan she already has, so a field here would be a second place
              to set something that is already set. */}
          <p className="font-body text-xs" style={{ color: "#54525C", marginTop: 6, paddingLeft: 26 }}>
            What's on the meal plan for tonight.
          </p>
        </div>

        <SavedIndicator show={savedFlash} className="mt-4 text-right" fontFamily={FONT} />
      </div>
    </section>
  );
};

export default BriefingBuilder;
